import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { detectBrowserCountry, getCurrencyFor, usesImperial } from '@/lib/i18n';
import { useAuth } from '@/context/AuthContext';

/**
 * Global country context.
 *
 * Priority order for resolving the *effective* country:
 *   1. Logged-in user's `country` (from AuthContext, injected via `setUserCountry`).
 *   2. Explicit user preference saved in localStorage ("rollm8s_country").
 *   3. Server-side IP detection via GET /api/geo/detect (cached once per session).
 *   4. Browser locale guess (`detectBrowserCountry()`).
 *   5. "GB" fallback.
 *
 * Also exposes `scope`, which drives discovery filtering:
 *   - "local"  → filter content to `country`
 *   - "global" → show everything (all countries)
 *   - "custom" → filter to `customCountry` (set via `setCountry(code)`),
 *                used by the Events page country-filter dropdown.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_COUNTRY = 'rollm8s_country';
const LS_SCOPE = 'rollm8s_discovery_scope';
const LS_CUSTOM = 'rollm8s_custom_country';
const SS_DETECTED = 'rollm8s_detected_country';

const GeoContext = createContext(null);

export const GeoProvider = ({ children }) => {
  // Auto-detected / browser-guessed country (no user override yet).
  const [detectedCountry, setDetectedCountry] = useState(
    () => sessionStorage.getItem(SS_DETECTED) || detectBrowserCountry()
  );
  // User override (profile or explicit selection). Null = no override.
  const [userCountry, setUserCountryState] = useState(
    () => localStorage.getItem(LS_COUNTRY) || null
  );
  // Discovery scope: local | global | custom
  const [scope, setScopeState] = useState(
    () => localStorage.getItem(LS_SCOPE) || 'local'
  );
  // Custom-country selection (used when scope==='custom'). null when unset.
  const [customCountry, setCustomCountryState] = useState(
    () => localStorage.getItem(LS_CUSTOM) || null
  );
  const [loading, setLoading] = useState(true);

  // Auto-bind logged-in user's profile country as the override. This is what
  // makes the Events page country filter default to the user's signup country
  // without each page having to plumb it through manually.
  const { user } = useAuth();
  useEffect(() => {
    const profileCountry = (user?.country || '').toUpperCase();
    if (profileCountry && profileCountry !== userCountry) {
      setUserCountryState(profileCountry);
      localStorage.setItem(LS_COUNTRY, profileCountry);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.country]);

  // Fetch server-side detection once per session.
  useEffect(() => {
    let cancelled = false;
    if (sessionStorage.getItem(SS_DETECTED)) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/geo/detect`);
        if (cancelled) return;
        if (data?.country) {
          sessionStorage.setItem(SS_DETECTED, data.country);
          setDetectedCountry(data.country);
        }
      } catch {
        // Silent — fall back to whatever we already had.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const setUserCountry = useCallback((code) => {
    const normalised = (code || '').toUpperCase() || null;
    setUserCountryState(normalised);
    if (normalised) localStorage.setItem(LS_COUNTRY, normalised);
    else localStorage.removeItem(LS_COUNTRY);
  }, []);

  const setScope = useCallback((next) => {
    setScopeState(next);
    localStorage.setItem(LS_SCOPE, next);
  }, []);

  /**
   * Pick a specific country to filter to (used by Events page dropdown).
   *   - code = ISO-2 → switches scope to 'custom' and persists the choice
   *   - code = ''    → switches scope to 'global' (= All countries)
   *   - code = null  → resets to 'local' (= user's own country)
   */
  const setCountry = useCallback((code) => {
    if (code === null || code === undefined) {
      setScopeState('local');
      setCustomCountryState(null);
      localStorage.setItem(LS_SCOPE, 'local');
      localStorage.removeItem(LS_CUSTOM);
      return;
    }
    const upper = (code || '').toUpperCase();
    if (!upper) {
      setScopeState('global');
      setCustomCountryState(null);
      localStorage.setItem(LS_SCOPE, 'global');
      localStorage.removeItem(LS_CUSTOM);
      return;
    }
    setScopeState('custom');
    setCustomCountryState(upper);
    localStorage.setItem(LS_SCOPE, 'custom');
    localStorage.setItem(LS_CUSTOM, upper);
  }, []);

  const country = userCountry || detectedCountry || 'GB';

  // Effective country filter applied to listings:
  //   local  → user's own country
  //   global → null (all)
  //   custom → the explicitly chosen country
  const filterCountry =
    scope === 'global' ? null :
    scope === 'custom' ? (customCountry || country) :
    country;

  const value = useMemo(() => ({
    country,                       // Effective country code (ISO-2)
    detectedCountry,               // What detection said (informational)
    userCountry,                   // null if user hasn't overridden
    setUserCountry,
    scope,                         // 'local' | 'global' | 'custom'
    setScope,
    customCountry,                 // Currently-selected custom country (or null)
    setCountry,                    // Drives the events filter dropdown
    filterCountry,
    currency: getCurrencyFor(country),
    imperial: usesImperial(country),
    loading,
  }), [country, detectedCountry, userCountry, setUserCountry, scope, setScope, customCountry, setCountry, filterCountry, loading]);

  return <GeoContext.Provider value={value}>{children}</GeoContext.Provider>;
};

export const useGeo = () => {
  const ctx = useContext(GeoContext);
  if (!ctx) throw new Error('useGeo must be used inside <GeoProvider>');
  return ctx;
};
